import { set } from 'lodash';
import { detailedCostingFieldpath } from '../fieldPath';
import { useProductCostingState } from '../ProductCostingContext';
import { roundingOff, checkIsNanAndNumber } from '../../../../Components/helper';
import type { LandedExchangeRateInfo } from '../exchange-rate.helpers';
import type { DetailedCostingBomRow, DetailedCostingExpandRow, DetailedCostingState } from '../types';

/**
 * The state this calculation walks. Wider than `DetailedCostingState` because callers pass the raw
 * costing document on first load, which carries `isDetailedCosting` alongside the grid state.
 */
export type DetailedCostingCalcState = DetailedCostingState & {
  isDetailedCosting?: boolean | undefined;
};

export interface UseDetailedCostingHooksResult {
  calculateDetailCosting: (
    state: DetailedCostingCalcState,
    currVal?: number | string | null,
    isFromOnload?: boolean,
    supplierCurrency?: string | null,
    exchangeRateInfo?: LandedExchangeRateInfo | null,
  ) => Promise<void>;
}

/**
 * Recalculates the Detailed Costing grid: per-row total price, and the per-BOM-type roll-ups of
 * quantity and total price.
 *
 * Rows priced in a currency other than the costing's own are divided by the exchange rate, while
 * `totalPriceBase` always keeps the unconverted figure. Excluded rows contribute to neither
 * roll-up but still keep their own totals.
 */
export default function useDetailedCostingHooks(): UseDetailedCostingHooksResult {
  const { setDetailedCostingState, internalState, productCostingstate } = useProductCostingState();
  const { primaryCurrValue } = internalState;

  const calculateDetailCosting = async (
    state: DetailedCostingCalcState,
    currVal?: number | string | null,
    isFromOnload = false,
    supplierCurrency: string | null = null,
    exchangeRateInfo: LandedExchangeRateInfo | null = null,
  ): Promise<void> => {
    const manipulatedData: DetailedCostingBomRow[] = [];

    const resolvedExchangeInfo = exchangeRateInfo || {};
    const { exchangedRate, manualAdded } = resolvedExchangeInfo;

    const currentCurrency = supplierCurrency ?? productCostingstate?.productInfo?.currency;

    const currencyValue = currVal ?? (manualAdded ? exchangedRate ?? 1 : primaryCurrValue ?? 1);
    const safeRate = Number(currencyValue) > 0 ? Number(currencyValue) : 1;

    const copyState = state;

    if (Array.isArray(copyState?.detailedCosting?.bomTypeArr) && (copyState?.detailedCosting?.bomTypeArr.length ?? 0) > 0) {
      for (const data of copyState.detailedCosting.bomTypeArr as DetailedCostingBomRow[]) {
        const newExpandData: DetailedCostingExpandRow[] = [];
        let overAllQuantity: string | number = '0';
        let overAllTotalPrice: string | number = '0';
        if (Array.isArray(data.expandData) && data.expandData.length > 0) {
          for (const element of data.expandData) {
            const { quantityPerSizCol } = element;
            const totalPrice = roundingOff((element.pricePerUnit as number) * (quantityPerSizCol as number), 2);
            element.totalPriceBase = totalPrice;
            if (data?.pricePerUnitCurrency !== currentCurrency) {
              /* `roundingOff` returns a string, so the original divided a string by a number and
                 leant on JavaScript's implicit coercion. Made explicit here — same result, and the
                 asymmetry is preserved: the converted branch stores a number, the else branch below
                 stores the string, exactly as the original did. */
              element.totalPrice = Number(totalPrice) / safeRate;
            } else {
              element.totalPrice = totalPrice;
            }

            if (!element?.isExcluded) {
              overAllTotalPrice = checkIsNanAndNumber(overAllTotalPrice) + checkIsNanAndNumber(totalPrice);
              overAllQuantity = checkIsNanAndNumber(overAllQuantity) + checkIsNanAndNumber(element?.quantityPerSizCol);
            }
            newExpandData.push(element);
          }
        }
        data.overAllQuantity = overAllQuantity;
        data.overAllTotalPrice = overAllTotalPrice;
        data.expandData = newExpandData;
        manipulatedData.push(data);
      }
    }
    if (isFromOnload) {
      set(copyState, detailedCostingFieldpath().DETAILED_COSTING_ACTIVE, copyState?.isDetailedCosting);
    }
    set(copyState, detailedCostingFieldpath().DETAILED_COSTING_DATA, manipulatedData);
    setDetailedCostingState((prevState) => ({ ...prevState, ...copyState }) as DetailedCostingState);
  };
  return { calculateDetailCosting };
}
